import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * Cart drawer component
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    // Signal open for gift sync
    this.dispatchEvent(new CustomEvent('cart-drawer:open'));

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document
        .querySelector('shopify-payment-terms')
        ?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

/**
 * ===== Free gift sync (Überraschung) – WORKING + SAFE =====
 * - Adds via /cart/add.js
 * - Removes via /cart/change.js using line item key
 * - Triggers on:
 *   - drawer open
 *   - add-to-cart event
 *   - quantity change/remove interactions in drawer
 *   - theme cart lifecycle events (if present)
 *
 * Key: debounce + short delay to avoid racing the theme's own cart updates.
 */
(function () {
  document.addEventListener('DOMContentLoaded', function () {
    const drawerEl = document.querySelector('cart-drawer-component.cart-drawer');
    if (!drawerEl) return;

    const giftVariantId = Number(drawerEl.dataset.giftVariantId || 0);
    const targetQty = Number(drawerEl.dataset.giftTargetQty || 0);

    if (!giftVariantId || !targetQty) {
      console.warn('[FreeGift] Missing data-gift-variant-id or data-gift-target-qty');
      return;
    }

    let syncing = false;
    let timer = null;

    function debounce(fn, wait) {
      return function () {
        clearTimeout(timer);
        timer = setTimeout(fn, wait);
      };
    }

    function fetchCart() {
      return fetch('/cart.js', { credentials: 'same-origin' }).then((r) => {
        if (!r.ok) throw new Error('Cart fetch failed');
        return r.json();
      });
    }

    function countNonGiftQty(cart) {
      return cart.items.reduce((sum, item) => {
        // /cart.js => item.id is the variant id
        if (item.id === giftVariantId) return sum;
        return sum + item.quantity;
      }, 0);
    }

    function findGiftItem(cart) {
      return cart.items.find((item) => item.id === giftVariantId);
    }

    function syncGiftNow() {
      if (syncing) return;
      syncing = true;

      fetchCart()
        .then((cart) => {
          const nonGiftQty = countNonGiftQty(cart);
          const giftItem = findGiftItem(cart);
          const shouldHaveGift = nonGiftQty >= targetQty;

          // ADD (only if not already present)
          if (shouldHaveGift && !giftItem) {
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'same-origin',
              body: JSON.stringify({
                id: giftVariantId,
                quantity: 1,
                properties: { _free_gift: 'true' }
              })
            }).then(() => {
              document.dispatchEvent(new CustomEvent('cart:updated'));
            });
          }

          // REMOVE (use key via /cart/change.js)
          if (!shouldHaveGift && giftItem) {
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'same-origin',
              body: JSON.stringify({
                id: giftItem.key,
                quantity: 0
              })
            }).then(() => {
              document.dispatchEvent(new CustomEvent('cart:updated'));
            });
          }

          return null;
        })
        .catch((err) => console.error('[FreeGift] sync failed', err))
        .finally(() => {
          syncing = false;
        });
    }

    // Debounced sync with a small delay to let theme finish its cart Ajax update first
    const syncGift = debounce(() => setTimeout(syncGiftNow, 250), 450);

    // Drawer opened
    drawerEl.addEventListener('cart-drawer:open', syncGift);

    // Add-to-cart
    document.addEventListener(CartAddEvent.eventName, syncGift);

    // Theme cart lifecycle events (if your theme emits them)
    ['cart:updated', 'cart:change', 'cart:refresh'].forEach((evt) => {
      document.addEventListener(evt, syncGift);
    });

    // IMPORTANT: Listen to real user interactions that change qty/remove in the drawer
    drawerEl.addEventListener('change', function (event) {
      const el = event.target;
      if (!el) return;

      // common quantity inputs
      if (el.name === 'updates[]' || el.name === 'quantity' || el.closest('.cart-items__quantity')) {
        syncGift();
      }
    });

    drawerEl.addEventListener('click', function (event) {
      const t = event.target;
      if (!t) return;

      // remove line item
      if (t.closest('.cart-items__remove')) {
        syncGift();
        return;
      }

      // plus/minus controls
      if (t.closest('.quantity-selector') || t.closest('.quantity__button')) {
        syncGift();
      }
    });

    // Initial sync
    syncGift();
  });
})();
